import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Input,
  Metric,
  Table,
  viLabel,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { ExportButton } from '@/components/common/ExportButton';
import { useViewport } from '@/lib/viewport';
import { useScope } from '@/lib/scope';
import { useMockTable } from '@/hooks/useMockTable';
import { useForecastResults } from '@/hooks/useForecast';
import { reportService } from '@/services/reportService';
import { formatNumber, toISODate } from '@/lib/utils';
import { useState } from 'react';

const HORIZON_DAYS = 14;

interface CustomerRow {
  id: string;
  code: string;
  name: string;
  tier: string;
  qty: number;
  workload_hours: number;
  share_pct: number;
  days: number;
  low_conf: number;
}

export function CustomerListPage() {
  const { isMobile, vw } = useViewport();
  const { currentWarehouse } = useScope();
  const navigate = useNavigate();
  const customers = useMockTable('customers');
  const results = useForecastResults({ warehouseId: currentWarehouse?.id });
  const [q, setQ] = useState('');

  const today = toISODate(new Date());
  const horizonEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + HORIZON_DAYS - 1);
    return toISODate(d);
  }, [today]);

  const horizonResults = useMemo(
    () => results.filter((r) => r.date >= today && r.date <= horizonEnd),
    [results, today, horizonEnd],
  );

  const breakdown = useMemo(
    () => reportService.byCustomer({ results: horizonResults, customers }),
    [horizonResults, customers],
  );

  const rows: CustomerRow[] = useMemo(() => {
    return breakdown
      .map((b) => {
        const c = customers.find((cu) => cu.id === b.key);
        const lowConf = horizonResults.filter(
          (r) => r.customer_id === b.key && r.confidence.is_low_confidence,
        ).length;
        return {
          id: b.key,
          code: c?.code ?? b.key.slice(-6),
          name: c?.name ?? b.label,
          tier: c?.tier ?? 'LONGTAIL',
          qty: b.qty,
          workload_hours: b.workload_hours,
          share_pct: b.share_pct,
          days: b.count,
          low_conf: lowConf,
        };
      })
      .filter((r) => {
        if (!q) return true;
        const needle = q.toLowerCase();
        return (r.name + ' ' + r.code).toLowerCase().includes(needle);
      });
  }, [breakdown, customers, horizonResults, q]);

  const totalQty = rows.reduce((s, r) => s + r.qty, 0);
  const totalHours = rows.reduce((s, r) => s + r.workload_hours, 0);
  const keyCount = rows.filter((r) => r.tier === 'KEY').length;
  const lowConfCount = rows.filter((r) => r.low_conf > 0).length;

  const cols: Column<CustomerRow>[] = [
    {
      header: 'Khách hàng',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--bx-text-dark)' }}>{r.name}</div>
          <div
            style={{
              fontSize: 10.5,
              color: 'var(--bx-text-muted)',
              fontFamily: 'var(--bx-font-mono)',
            }}
          >
            {r.code}
          </div>
        </div>
      ),
    },
    {
      header: 'Tier',
      priority: 2,
      render: (r) => (
        <Badge
          kind={r.tier === 'KEY' ? 'APPROVED' : r.tier === 'GROWTH' ? 'INFO' : 'DRAFT'}
          size="sm"
        >
          {viLabel(r.tier)}
        </Badge>
      ),
    },
    {
      header: 'Đơn 14N',
      align: 'right',
      mono: true,
      render: (r) => formatNumber(r.qty),
    },
    {
      header: 'Workload',
      align: 'right',
      mono: true,
      priority: 2,
      render: (r) => r.workload_hours.toFixed(1) + 'h',
    },
    {
      header: '% Share',
      align: 'right',
      mono: true,
      priority: 2,
      render: (r) => r.share_pct.toFixed(1) + '%',
    },
    {
      header: 'Độ tin cậy',
      priority: 3,
      render: (r) =>
        r.low_conf > 0 ? (
          <Badge kind="WARNING" size="sm">
            {r.low_conf} thấp
          </Badge>
        ) : (
          <Badge kind="APPROVED" size="sm">
            Tốt
          </Badge>
        ),
    },
    {
      header: '',
      width: 60,
      render: (r) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="sm"
            variant="ghost-primary"
            iconRight="arrowRight"
            onClick={() => navigate(`/planner/forecast/customer/${r.id}`)}
          >
            Mở
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Lập kế hoạch', 'Dự báo', 'Khách hàng']}
        title="Báo cáo theo khách hàng"
        subtitle={
          currentWarehouse
            ? `${currentWarehouse.code} · ${rows.length} khách hàng có forecast trong ${HORIZON_DAYS} ngày tới`
            : 'Chọn kho ở thanh trên'
        }
        actions={
          <ExportButton
            rows={rows.map((r) => ({
              code: r.code,
              name: r.name,
              tier: r.tier,
              qty: r.qty,
              workload_hours: r.workload_hours.toFixed(1),
              share_pct: r.share_pct.toFixed(1),
              days: r.days,
              low_confidence: r.low_conf,
            }))}
            columns={[
              { header: 'Mã KH', key: 'code' },
              { header: 'Tên KH', key: 'name' },
              { header: 'Tier', key: 'tier' },
              { header: 'Đơn (14N)', key: 'qty' },
              { header: 'Workload (h)', key: 'workload_hours' },
              { header: '% Share', key: 'share_pct' },
              { header: 'Số ngày', key: 'days' },
              { header: 'Low conf', key: 'low_confidence' },
            ]}
            filename={`customers-${currentWarehouse?.code ?? 'wh'}-${today}`}
          />
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <div
          style={{
            display: 'grid',
            gap: 12,
            marginBottom: 16,
            gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          }}
        >
          <Metric
            label="Tổng đơn 14N"
            value={formatNumber(totalQty)}
            sublabel="forecast"
          />
          <Metric
            label="Workload tổng"
            value={`${formatNumber(Math.round(totalHours))}h`}
            sublabel={`≈ ${(totalHours / 8).toFixed(1)} ca`}
          />
          <Metric label="Khách hàng KEY" value={keyCount} sublabel="trọng điểm" />
          <Metric
            label="KH cảnh báo"
            value={lowConfCount}
            sublabel="độ tin cậy thấp"
            deltaTone={lowConfCount > 0 ? 'down' : 'flat'}
          />
        </div>

        <Card padding={0}>
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--bx-border-muted)',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <div style={{ width: 280 }}>
              <Input
                icon="search"
                placeholder="Tìm khách hàng…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                size="sm"
              />
            </div>
            <div style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" icon="filter">
              Bộ lọc khác
            </Button>
          </div>
          <Table
            columns={cols}
            rows={rows}
            getRowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/planner/forecast/customer/${r.id}`)}
            emptyText={
              horizonResults.length === 0
                ? 'Chưa có dự báo. Chạy forecast trước.'
                : 'Không có khách hàng khớp'
            }
          />
        </Card>
      </div>
    </div>
  );
}
